import { useEffect, useMemo } from 'react';
import { twJoin } from 'tailwind-merge';

import { TrustSignalsBadgeContainer } from '@dbt-labs/metadata-shared';
import { RyeconClock, RyeconRerun, RyeconTable } from '@dbt-labs/sourdough';
import { RyeconTableRow } from '@dbt-labs/sourdough';

import { FreshnessStatus, SourceAppliedStateNode } from '@/__generated__/graphql';
import DetailTabs, { TabInfo, TabType } from '@/components/DetailTabs';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { DescriptionChangesProvider } from '@/context/DescriptionChangesProvider';
import { useMissingDataHeader } from '@/context/MissingDataHeaderProvider';
import { ResourceInfoProvider } from '@/context/ResourceInfoProvider';
import { useSource } from '@/hooks/dbtStrategy/useSource';
import { useRecommendationsCount } from '@/hooks/discovery/recommendationsCount';
import { GenericResourceNode } from '@/hooks/discovery/types';
import { useValidatedParams } from '@/hooks/params';
import { useSetMissingCatalog } from '@/hooks/useSetMissingCatalog';
import { useSetMissingTests } from '@/hooks/useSetMissingTests';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { getDatabaseObjectType } from '@/util/database';
import { formatAbsoluteLocalDate, formatDateCommon } from '@/util/date';
import { computeSourceFreshness } from '@/util/dbt/freshness';
import { getPatchPath } from '@/util/dbt/patchPath';
import { toTitleCase } from '@/util/string';
import { useWarehouseHeaderIcon } from '../../hooks/useWarehouseHeaderIcon';
import {
  getResourceTypeHeaderIcon,
  ResourceDetailsHeader,
  TextIconPair,
} from '../Header';
import { LatestFreshnessStatusSection } from '../Sections/LatestStatusSection';
import { ColumnsView } from '../Tabs/ColumnsView';
import { EvaluatorView } from '../Tabs/EvaluatorView';
import { GeneralView } from '../Tabs/GeneralView';

const useTabs = (violationsCount: number, enableRecommendations: boolean) => {
  return useMemo(() => {
    const tabs: TabInfo[] = [
      { type: 'general' },
      { type: 'columns' },
      ...(enableRecommendations
        ? [{ type: 'evaluator' as TabType, count: violationsCount || undefined }]
        : []),
    ];

    return tabs;
  }, [enableRecommendations, violationsCount]);
};

type ResourceParams = {
  uniqueId: ParsedUniqueId;
};

export const SourceView = ({ uniqueId }: ResourceParams) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const { projectId, environmentType } = useValidatedParams();
  const sourceQuery = useSource(uniqueId);
  const { setMissingSourceFreshness } = useMissingDataHeader();

  const { environmentId } = useDbtCloudProject();
  const source = sourceQuery.fetching ? null : sourceQuery.data;

  const warehouseHeaderIcon = useWarehouseHeaderIcon({
    uniqueId,
    environmentType,
  });

  const [recommendationCount] = useRecommendationsCount({
    environmentId,
    uniqueId: uniqueId.raw,
  });

  const tabs = useTabs(
    recommendationCount.data?.recommendations?.rules?.totalCount || 0,
    hasAccountFeature('explorerRecommendations'),
  );

  const sourceName = uniqueId.remainder;
  const date = source?.applied?.freshness?.snapshottedAt
    ? new Date(source.applied.freshness.snapshottedAt)
    : undefined;
  const updatedAt = date && formatAbsoluteLocalDate(date);
  const updatedAtUtc = date && formatDateCommon(date).utc;
  const objectType =
    source?.applied?.catalog?.type &&
    getDatabaseObjectType(source.applied?.catalog.type);
  const rowCount = source?.applied?.catalog?.rowCountStat ?? undefined;
  const headerIcons: TextIconPair[] = [getResourceTypeHeaderIcon('source')];
  if (updatedAt) {
    headerIcons.push({
      ryecon: RyeconClock,
      text: `Last run ${updatedAt}`,
      tooltip: updatedAtUtc || undefined,
    });
  }
  const status = computeSourceFreshness(source?.applied?.freshness ?? null);
  if (status) {
    const classNames = twJoin(
      status === FreshnessStatus.Pass && 'text-fgSuccess',
      status === FreshnessStatus.Error && 'text-fgDanger',
      status === FreshnessStatus.Warn && 'text-fgWarning',
    );
    headerIcons.push({
      ryecon: RyeconRerun,
      key: 'status',
      Contents: () => (
        <span>
          <span>Freshness: </span>
          <span className={classNames}>{toTitleCase(status.toString())}</span>
        </span>
      ),
    });
  }
  if (objectType) {
    headerIcons.push({
      ryecon: RyeconTable,
      text: `${objectType}`,
    });
  }
  if (warehouseHeaderIcon) {
    headerIcons.push(warehouseHeaderIcon);
  }
  if (rowCount) {
    headerIcons.push({
      ryecon: RyeconTableRow,
      text: `${rowCount.toLocaleString()} rows`,
    });
  }
  const appliedData = source?.applied;
  useSetMissingTests(appliedData);
  useSetMissingCatalog(appliedData);
  useEffect(() => {
    const isMissingSourceFreshness =
      !!appliedData && appliedData.freshness?.freshnessStatus === null;
    setMissingSourceFreshness(isMissingSourceFreshness);
    return () => {
      setMissingSourceFreshness(false);
    };
  }, [setMissingSourceFreshness, appliedData]);

  const sourceCollectionName = source?.applied?.sourceName;
  const sourceTableName = source?.applied?.name;

  if (sourceQuery.error) return <>{sourceQuery.error.stack}</>;
  return (
    <>
      <ResourceDetailsHeader
        uniqueId={uniqueId.raw}
        title={sourceName}
        headerIcons={headerIcons}
        packageName={uniqueId.packageName}
        files={[appliedData?.patchPath, appliedData?.filePath]}
        name={appliedData?.name ?? ''}
        resourceType="source"
        sourceName={appliedData?.sourceName}
        updatedDate={date}
        rightAdornment={
          <TrustSignalsBadgeContainer
            resourceType="source"
            trustSignals={{
              healthIssues: source?.applied?.healthIssues,
              additionalMetadata: {
                freshnessChecked: source?.applied?.freshness?.freshnessChecked,
              },
            }}
            size="lg"
            className="mt-1"
          />
        }
      />
      <DetailTabs tabs={tabs} show={!!source}>
        {(activeTab) => {
          return (
            <ResourceInfoProvider
              resourceType="source"
              resourceIdentifiers={
                sourceCollectionName && sourceTableName
                  ? [sourceCollectionName, sourceTableName]
                  : []
              }
              filePath={getPatchPath(source?.applied)}
              latestGitSha={source?.latestGitSha}
            >
              {activeTab === 'general' && (
                <>
                  <LatestFreshnessStatusSection
                    resourceType="source"
                    applied={source?.applied as SourceAppliedStateNode}
                    checkCompletedAt={updatedAt}
                    checkCompletedAtUtc={updatedAtUtc}
                  />
                  <DescriptionChangesProvider
                    entityType="source_table"
                    projectId={projectId}
                  >
                    <GeneralView
                      resource={source?.applied as GenericResourceNode}
                      uniqueId={uniqueId}
                      description={source?.applied?.description}
                      meta={source?.applied?.meta}
                    />
                  </DescriptionChangesProvider>
                </>
              )}
              {activeTab === 'columns' && source?.applied && (
                <ColumnsView resource={source.applied} />
              )}
              {activeTab === 'evaluator' && <EvaluatorView uniqueId={uniqueId.raw} />}
            </ResourceInfoProvider>
          );
        }}
      </DetailTabs>
    </>
  );
};
